import {CHART_HEIGHT, ChartTitle, formatMemory, MemoryChart, parseMemoryRow, rssSummary, rssToMb} from "./MemoryChart";
import React, {useState} from "react";

// Shared rendering for the per-broker benchmark result pages (fan-in, fan-out, and any
// later scenario). The results files under xmq_test_results/ all share one format:
// a preamble, then one block per broker with "Server:"/"Version:"/"CPU Load:"/"Max RAM:"
// meta followed by an interval table. Keeping the parser and chart here means a new
// scenario page is a few lines rather than another copy of this file.

export const SERVER_COLORS = {
    XMQ: "#1f77b4",
    Mosquitto: "#2ca02c",
    EMQX: "#d62728",
    NanoMQ: "#ff7f0e",
    FlashMQ: "#9467bd",
    // Checked for normal-vision separation against the five above. Red against green (EMQX,
    // Mosquitto) is the palette's known colour-blind weak pair, which the per-broker marker shapes
    // are there to carry. VerneMQ and TBMQ were measured and are not charted: VerneMQ failed most
    // of the scenarios, TBMQ ran at milliseconds to tens of seconds.
    HiveMQ: "#8d6e00",
};

// A result file's "Server:" heading is charted when it names one of these exactly; anything after
// the name ("XMQ (0.9.13, not charted)") keeps the run in the record and off the chart. Derived
// from the colours, so a broker cannot be charted without one.
export const KNOWN_BROKERS = new RegExp(`^(${Object.keys(SERVER_COLORS).join("|")})$`);

// If a file contains more than one run for a broker, use the last for the comparison.
export function selectCurrentBrokerRuns(runs)
{
    const byBroker = new Map();
    for (const run of runs)
        byBroker.set(run.name, run);
    return [...byBroker.values()];
}

// The preamble's conditions line - the one before "Date:" - is the only place a result file says
// what its numbers were measured at: QoS, payload size, that the run is an MQTT 5 one, the connect
// rate. The parser dropped the preamble, so a reader of a test page had no way to see any of it.
export function parseRunConditions(text)
{
    let previous = "";
    for (const raw of text.split("\n")) {
        const line = raw.trim();
        if (/^Date:/.test(line))
            return previous;
        if (line !== "")
            previous = line;
    }
    return "";
}

// "Server:" also appears in the preamble describing the host, so only lines naming a
// known broker start a result block; anything else closes the current one.
export function parseBrokerResult(text)
{
    const servers = [];
    let current = null;

    for (const raw of text.split("\n")) {
        const line = raw.trim();
        if (line === "" || line.startsWith("Interval") || /^─+$/.test(line))
            continue;

        let m;
        if ((m = line.match(/^Server:\s*(.+)$/))) {
            const name = m[1].trim();
            if (KNOWN_BROKERS.test(name)) {
                current = {name, meta: {}, dataPoints: [], memoryPoints: []};
                servers.push(current);
            } else {
                current = null;
            }
            continue;
        }

        if ((m = line.match(/^Average\s+(\d+)\s+(\d+)us\s+(\d+)(?:\s+[\d.]+\s*(?:Mb|Gb))*$/i)) && current) {
            current.averageCount = parseInt(m[1], 10);
            current.averageLatency = parseInt(m[2], 10);
            current.averageRate = parseInt(m[3], 10);
            continue;
        }

        const memory = current && parseMemoryRow(line);
        if (memory) {
            current.memoryPoints.push(memory);
            continue;
        }

        // The percentile section xmq_scn prints after the median. Runs recorded before it was
        // carried in the result files have none, and the table then shows the median alone.
        if (current && line === "Percentiles") {
            current.readingPercentiles = true;
            continue;
        }
        if (current && current.readingPercentiles) {
            if ((m = line.match(/^(p\d+(?:\.\d+)?|max)\s+(\d+)(us|ms)$/))) {
                current.percentiles = current.percentiles || {};
                current.percentiles[m[1]] = parseInt(m[2], 10) * (m[3] === "ms" ? 1000 : 1);
                continue;
            }
            current.readingPercentiles = false;
        }

        if (current && (m = line.match(/^Median\s+(\d+)us(?:\s+.*)?$/))) {
            current.medianLatency = parseInt(m[1], 10);
            continue;
        }

        // The broker's RSS in the interval is the table's fifth column; older files carried it in a
        // separate Memory block, which parseMemoryRow above still reads.
        if ((m = line.match(/^(\d+)ms\s+(\d+)\s+(\d+)us\s+(\d+)(?:\s+([\d.]+)\s*(Mb|Gb))?$/i)) && current) {
            const interval = parseInt(m[1], 10);
            current.dataPoints.push({
                interval,
                count: parseInt(m[2], 10),
                latency: parseInt(m[3], 10),
                rate: parseInt(m[4], 10),
            });
            if (m[5])
                current.memoryPoints.push({interval, rssMb: rssToMb(m[5], m[6])});
            continue;
        }

        if ((m = line.match(/^([A-Za-z ]+?):\s+(.+)$/)) && current)
            current.meta[m[1].trim()] = m[2].trim();
    }

    // The conditions travel on the array: the pages hand what parseBrokerResult returns straight to
    // BrokerBenchmark, so a page does not have to know about them to have them shown.
    const runs = selectCurrentBrokerRuns(servers);
    runs.conditions = parseRunConditions(text);
    return runs;
}

export function formatLatency(us)
{
    if (us >= 1_000_000)
        return `${(us / 1_000_000).toFixed(us >= 10_000_000 ? 0 : 1)}s`;
    if (us >= 1000)
        return `${(us / 1000).toFixed(1)}ms`;
    return `${us}us`;
}

// The median is its own row in the result files, and p50 in the percentile section. Either can
// be the one a run recorded; a run with neither keeps the average and shows a dash here.
const medianLatency = (s) => s.medianLatency ?? s.percentiles?.p50;

// A percentile the run did not record is a dash rather than an absent column: the table is one
// table for every broker on the page, and only the newer runs carry the section.
const percentileLatency = (s, key) => {
    const value = s.percentiles?.[key];
    return value === undefined ? "—" : formatLatency(value);
};

const formatRate = (r) => (r >= 1000 ? `${(r / 1000).toFixed(1)}K/s` : `${r}/s`);
const formatInterval = (ms) => `${Math.round(ms / 1000)}s`;

// A marker drawn on the points and repeated in the legend helps distinguish the broker lines. Each
// broker keeps its own on every chart. XMQ and FlashMQ often measure the same, so theirs are a ring
// and a cross: drawn on one another, both stay visible.
const BROKER_MARKERS = {
    XMQ: "ring",
    FlashMQ: "cross",
    Mosquitto: "square",
    EMQX: "triangle",
    HiveMQ: "diamond",
    NanoMQ: "circle",
};

export const markerFor = (name) => BROKER_MARKERS[name] || "circle";

/**
 * One marker, centred on (cx, cy). Used on the chart, in the legend and in the hover tooltip, so
 * that the three cannot disagree.
 */
export function SeriesMarker({shape, cx, cy, size = 10.5, fill, stroke = "#fff", strokeWidth = 1.25})
{
    const r = size / 2;
    const common = {fill, stroke, strokeWidth};
    switch (shape) {
        case "square":
            return <rect x={cx - r} y={cy - r} width={size} height={size} {...common}/>;
        case "triangle":
            return <polygon {...common}
                            points={`${cx},${cy - r * 1.25} ${cx + r * 1.15},${cy + r * 0.95} ${cx - r * 1.15},${cy + r * 0.95}`}/>;
        case "diamond":
            return <polygon {...common}
                            points={`${cx},${cy - r * 1.3} ${cx + r * 1.3},${cy} ${cx},${cy + r * 1.3} ${cx - r * 1.3},${cy}`}/>;
        case "ring":
            return <circle cx={cx} cy={cy} r={r + 1} fill="none" stroke={fill} strokeWidth={Math.max(strokeWidth, 1.75)}/>;
        case "cross":
            return <path {...common} strokeWidth={strokeWidth + 1} stroke={fill} fill="none"
                         d={`M${cx - r},${cy - r} L${cx + r},${cy + r} M${cx - r},${cy + r} L${cx + r},${cy - r}`}/>;
        default:
            return <circle cx={cx} cy={cy} r={r} {...common}/>;
    }
}

/**
 * The legend entry: a sample of the line, dashes and all, with its marker sitting on it.
 */
export function LegendMarker({shape, color, dashed})
{
    return (
        <svg width="30" height="16" style={{flex: "none"}} aria-hidden="true">
            <line x1="1" x2="29" y1="8" y2="8" stroke={color} strokeWidth="2"
                  strokeDasharray={dashed ? "5 3" : undefined}/>
            <SeriesMarker shape={shape} cx={15} cy={8} fill={color}/>
        </svg>
    );
}

function LatencyChart({servers, width = 760, height = CHART_HEIGHT})
{
    const [hover, setHover] = useState(null);

    const margin = {top: 16, right: 16, bottom: 44, left: 96};
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const allPoints = servers.flatMap((s) => s.dataPoints);
    if (allPoints.length === 0)
        return null;

    const xMax = Math.max(...allPoints.map((p) => p.interval));
    const yMinRaw = Math.min(...allPoints.map((p) => p.latency));
    const yMaxRaw = Math.max(...allPoints.map((p) => p.latency));

    // Brokers here can differ by five orders of magnitude in the same scenario; a linear
    // axis would flatten the faster one onto the baseline and show nothing.
    const useLog = yMinRaw > 0 && yMaxRaw / yMinRaw > 50;
    const yMin = useLog ? Math.pow(10, Math.floor(Math.log10(yMinRaw))) : 0;
    const yMax = useLog ? Math.pow(10, Math.ceil(Math.log10(yMaxRaw))) : yMaxRaw;

    const xScale = (x) => margin.left + (xMax === 0 ? 0 : x / xMax) * innerW;
    const yScale = (y) => {
        const v = Math.max(y, yMin || 1);
        const t = useLog
            ? (Math.log10(v) - Math.log10(yMin)) / (Math.log10(yMax) - Math.log10(yMin) || 1)
            : (v - yMin) / (yMax - yMin || 1);
        return margin.top + innerH - t * innerH;
    };

    const intervals = [...new Set(allPoints.map((p) => p.interval))].sort((a, b) => a - b);
    const xTicks = intervals.filter((_, i) => i % 2 === 0);
    const yTicks = [];
    if (useLog) {
        for (let p = Math.log10(yMin); p <= Math.log10(yMax) + 0.001; p++)
            yTicks.push(Math.pow(10, p));
    } else {
        const step = (yMax - yMin) / 5;
        for (let i = 0; i <= 5; i++) yTicks.push(Math.round(yMin + step * i));
    }

    const onMove = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const px = ((event.clientX - rect.left) / rect.width) * width;
        if (px < margin.left || px > width - margin.right) { setHover(null); return; }
        const value = ((px - margin.left) / innerW) * xMax;
        let nearest = intervals[0];
        for (const i of intervals)
            if (Math.abs(i - value) < Math.abs(nearest - value)) nearest = i;
        setHover(nearest);
    };

    const hoverRows = hover === null ? [] : servers
        .map((s, i) => ({
            name: s.name, shape: markerFor(s.name),
            point: s.dataPoints.find((p) => p.interval === hover)
        }))
        .filter((r) => r.point);

    const tipW = 190;
    const tipH = 20 + hoverRows.length * 16;
    const tipX = hover === null ? 0 : Math.min(xScale(hover) + 10, width - margin.right - tipW);
    const tipY = margin.top + 6;

    return (
        <svg viewBox={`0 0 ${width} ${height}`}
             style={{width: "100%", maxWidth: width, height: "auto"}}
             onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
            <title>Average latency per interval, by broker</title>
            <text transform={`translate(16, ${margin.top + innerH / 2}) rotate(-90)`}
                  textAnchor="middle" fontSize="11" fill="#777">
                Latency
            </text>

            {yTicks.map((t, i) => (
                <g key={`y${i}`}>
                    <line x1={margin.left} x2={width - margin.right} y1={yScale(t)} y2={yScale(t)} stroke="#eee"/>
                    <text x={margin.left - 8} y={yScale(t)} textAnchor="end" dominantBaseline="middle"
                          fontSize="11" fill="#555">{formatLatency(Math.round(t))}</text>
                </g>
            ))}
            {xTicks.map((t, i) => (
                <text key={`x${i}`} x={xScale(t)} y={height - margin.bottom + 16} textAnchor="middle"
                      fontSize="11" fill="#555">{formatInterval(t)}</text>
            ))}
            <text x={margin.left + innerW / 2} y={height - 6} textAnchor="middle" fontSize="11" fill="#777">
                elapsed
            </text>

            <line x1={margin.left} x2={width - margin.right} y1={height - margin.bottom}
                  y2={height - margin.bottom} stroke="#999"/>
            <line x1={margin.left} x2={margin.left} y1={margin.top} y2={height - margin.bottom} stroke="#999"/>

            {hover !== null && (
                <line x1={xScale(hover)} x2={xScale(hover)} y1={margin.top} y2={height - margin.bottom}
                      stroke="#bbb" strokeDasharray="3 3"/>
            )}

            {servers.map((s) => (
                <polyline key={s.name} fill="none" stroke={SERVER_COLORS[s.name] || "#333"} strokeWidth="2"
                          points={s.dataPoints.map((p) => `${xScale(p.interval)},${yScale(p.latency)}`).join(" ")}/>
            ))}

            {servers.map((s, i) => s.dataPoints.map((p, j) => (
                <SeriesMarker key={`${s.name}-${j}`} shape={markerFor(s.name)}
                              cx={xScale(p.interval)} cy={yScale(p.latency)}
                              fill={SERVER_COLORS[s.name] || "#333"}/>
            )))}

            {hover !== null && hoverRows.map((r) => (
                <SeriesMarker key={r.name} shape={r.shape} size={16} strokeWidth={2}
                              cx={xScale(hover)} cy={yScale(r.point.latency)}
                              fill={SERVER_COLORS[r.name] || "#333"}/>
            ))}

            {hover !== null && hoverRows.length > 0 && (
                <g>
                    <rect x={tipX} y={tipY} width={tipW} height={tipH} rx="4" fill="#fff" stroke="#ccc" opacity="0.96"/>
                    <text x={tipX + 8} y={tipY + 14} fontSize="11" fill="#555">{formatInterval(hover)} elapsed</text>
                    {hoverRows.map((r, i) => (
                        <g key={r.name}>
                            <SeriesMarker shape={r.shape} cx={tipX + 12} cy={tipY + 26 + i * 16}
                                          fill={SERVER_COLORS[r.name] || "#333"} stroke="none" strokeWidth={0}/>
                            <text x={tipX + 22} y={tipY + 30 + i * 16} fontSize="11" fill="#333">
                                {r.name} {formatLatency(r.point.latency)} &middot; {formatRate(r.point.rate)}
                            </text>
                        </g>
                    ))}
                </g>
            )}
        </svg>
    );
}

// Broker tables are comparable across scenarios only when the rows are in the same order. The
// result files carry each campaign's order, so the table sorts by broker name and lets a reader
// reverse it; the header stays the same for every page using BrokerBenchmark.
export function sortServersByName(servers, ascending = true)
{
    return [...servers].sort((a, b) =>
        a.name.localeCompare(b.name) * (ascending ? 1 : -1));
}

export function BrokerBenchmark({servers, targetRate,
                                 latencyTitle = "Delivery latency over the test",
                                 memoryTitle = "Broker memory (RSS) over the test"})
{
    const [nameAscending, setNameAscending] = useState(true);
    if (!servers || servers.length === 0)
        return null;

    const ordered = sortServersByName(servers, nameAscending);
    const cell = {border: "1px solid #ccc", padding: "4px 8px", verticalAlign: "top"};
    const head = {...cell, background: "#f0f0f0", fontWeight: "bold", textAlign: "left"};
    const sortableHead = {...head, cursor: "pointer", userSelect: "none"};
    const table = {borderCollapse: "collapse", margin: "8px 0 16px 0"};

    return (
        <div>
            {servers.conditions &&
                <p style={{fontSize: "0.9em", color: "#666", margin: "8px 0 0 0"}}>
                    {servers.conditions}
                </p>}
            <table style={table}>
                <tbody>
                <tr>
                    <td style={sortableHead} onClick={() => setNameAscending((value) => !value)}
                        title="Sort by broker name" aria-sort={nameAscending ? "ascending" : "descending"}>
                        Broker {nameAscending ? "▲" : "▼"}
                    </td>
                    <td style={head}>Version</td>
                    <td style={head}>Messages</td>
                    <td style={head}>Achieved rate</td>
                    <td style={head}>Avg latency</td>
                    <td style={head}>Median</td>
                    <td style={head}>p99</td>
                    <td style={head}>p99.9</td>
                    <td style={head}>CPU</td>
                    <td style={head}>Avg RAM</td>
                    <td style={head}>Median RAM</td>
                    <td style={head}>Max RAM</td>
                </tr>
                {ordered.map((s, i) => {
                    const missed = targetRate && s.averageRate && s.averageRate < targetRate * 0.97;
                    const ram = rssSummary(s.memoryPoints);
                    return (
                        <tr key={s.name}>
                            <td style={{...cell, whiteSpace: "nowrap"}}>
                                <LegendMarker shape={markerFor(s.name)}
                                              color={SERVER_COLORS[s.name] || "#333"}/>
                                {s.name}
                            </td>
                            <td style={cell}>{s.meta["Version"] || "-"}</td>
                            <td style={cell}>{s.averageCount?.toLocaleString()}</td>
                            <td style={cell}>
                                {s.averageRate?.toLocaleString()}/s
                                {missed && <span style={{color: "#b00", marginLeft: 6}}>(below target)</span>}
                                {s.meta["Status"] && <span style={{color: "#b00", marginLeft: 6}}
                                                           title={s.meta["Status"]}>(did not finish)</span>}
                            </td>
                            <td style={cell}>{formatLatency(s.averageLatency)}</td>
                            <td style={cell}>{medianLatency(s) === undefined ? "-" : formatLatency(medianLatency(s))}</td>
                            <td style={cell}>{percentileLatency(s, "p99")}</td>
                            <td style={cell}>{percentileLatency(s, "p99.9")}</td>
                            <td style={cell}>{s.meta["CPU Load"] || "-"}</td>
                            <td style={cell}>{ram ? formatMemory(ram.average) : "-"}</td>
                            <td style={cell}>{ram ? formatMemory(ram.median) : "-"}</td>
                            <td style={cell}>{s.meta["Max RAM"] || "-"}</td>
                        </tr>
                    );
                })}
                </tbody>
            </table>

            <div style={{display: "flex", gap: 16, margin: "12px 0 8px"}}>
                {ordered.map((s, i) => (
                    <div key={s.name} style={{display: "flex", alignItems: "center", gap: 4}}>
                        <LegendMarker shape={markerFor(s.name)}
                                      color={SERVER_COLORS[s.name] || "#333"}/>
                        <span>{s.name}</span>
                    </div>
                ))}
            </div>

            <ChartTitle>{latencyTitle}</ChartTitle>
            <LatencyChart servers={ordered}/>
            <ChartTitle>{memoryTitle}</ChartTitle>
            <MemoryChart series={ordered.map((s, i) => ({key: s.name, label: s.name, color: SERVER_COLORS[s.name] || "#333", shape: markerFor(s.name), points: s.memoryPoints}))}/>
            <p style={{fontSize: "0.9em", color: "#666"}}>
                Latency uses a logarithmic axis: the brokers differ by several orders of magnitude
                in this scenario, and a linear axis would flatten the faster ones onto the baseline.
                Hover the chart for per-interval values.
            </p>
        </div>
    );
}
